import React, { useEffect, useRef, useState } from "react";
import { Box, Text, ActionList } from "@primer/react";
import {
  MailIcon,
  DeviceMobileIcon,
  ChevronRightIcon,
} from "@primer/octicons-react";
import { phuHuynhApi } from "../../api/phuHuynhApi";
import UserAvatar from "../user-avatar";

interface PhuHuynhFamilyMember {
  id: string | number;
  ho_ten: string;
  quan_he: string;
  email: string;
  dien_thoai: string;
}

interface PhuHuynhFamilyProps {
  id: number;
  onMemberSelect?: (memberId: number) => void;
}

const PhuHuynhFamily: React.FC<PhuHuynhFamilyProps> = ({ id, onMemberSelect }) => {
  const [phuHuynhFamilys, setPhuHuynhFamily] = useState<PhuHuynhFamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const componentDidMount = useRef<boolean>(false);

  useEffect(() => {
    componentDidMount.current = true;
    handleReloadPhuHuynhFamily();
    return () => {
      componentDidMount.current = false;
    };
  }, [id]);

  const handleReloadPhuHuynhFamily = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const res = await phuHuynhApi.selectFamily(id);
      if (componentDidMount.current) {
        setIsLoading(false);
        if (res.is_success) {
          setPhuHuynhFamily(res.data);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error loading family data:", error);
    }
  };

  if (isLoading || phuHuynhFamilys.length === 0) return null;

  return (
    <ActionList showDividers>
      <ActionList.GroupHeading>Thành viên khác</ActionList.GroupHeading>

      {phuHuynhFamilys.map((member, index) => (
        <ActionList.Item
          key={index}
          sx={{
            py: 2,
            "&:hover": {
              backgroundColor: "canvas.subtle",
            },
            cursor: "pointer",
          }}
          onClick={() => onMemberSelect?.(Number(member.id))}
        >
          {/* Rest of the component remains the same */}
          <Box sx={{ display: "flex", width: "100%", mt: 2 }}>
            {/* Left column */}
            <Box sx={{ 
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
              minWidth: "32px",
            }}>
              <Box sx={{
                height: "24px",
                display: "flex",
                alignItems: "center",
                mb: 1,
              }}>
                <UserAvatar size="medium" fullName={member.ho_ten} />
              </Box>
              <Box sx={{
                width: "100%",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}>
                <MailIcon size={16} />
              </Box>
              <Box sx={{
                width: "100%",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <DeviceMobileIcon size={16} />
              </Box>
            </Box>

            {/* Middle column */}
            <Box flex={1}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                height: "24px",
                mb: 1,
              }}>
                <Text fontWeight="bold">{member.ho_ten}</Text>
                {member.quan_he && (
                  <Text as="span" sx={{
                    ml: 2,
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 0,
                    backgroundColor: 'accent.muted',
                    color: 'accent.fg',
                    display: "inline-flex",
                    alignItems: "center",
                  }}>
                    {member.quan_he}
                  </Text>
                )}
              </Box>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                height: "24px",
                mb: 1,
              }}>
                <Text fontSize={1}>{member.email}</Text>
              </Box>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                height: "24px",
              }}>
                <Text fontSize={1}>{member.dien_thoai}</Text>
              </Box>
            </Box>

            {/* Right column */}
            <Box sx={{
              ml: 2,
              display: "flex",
              alignItems: "center",
            }}>
              <ChevronRightIcon size={16} />
            </Box>
          </Box>
        </ActionList.Item>
      ))}
    </ActionList>
  );
};

export default PhuHuynhFamily;